<template>
    <div class="input-group">
        <label>{{ $t('homepage.psapSelect.label') }}</label>
        <v-autocomplete
            v-model="selected"
            :error-messages="errorMessages"
            clearable
            no-filter
            :items="selectableItems"
            :loading="loading"
            :search-input.sync="search"
            hide-no-data
            :item-text="itemName"
            item-value="id"
            :placeholder="$t('homepage.psapSelect.placeholder')"
            solo
            flat
            hide-details
        >
        </v-autocomplete>
    </div>
</template>

<script lang="ts">
import Vue from 'vue'

import { searchPsaps, Psap } from '@/services/psap-search'
import { getPsap } from '@/services/psaps'

export default Vue.extend({
    props: ['value', 'errorMessages'],
    data(): {
        loading: boolean
        search: string | null
        selected: number | null | false
        error: any
        serverPsaps: Partial<Psap>[]
    } {
        return {
            loading: false,
            search: null,
            selected: null,
            error: null,
            serverPsaps: []
        }
    },
    watch: {
        async value(value) {
            if (this.selected !== value && value !== false && value !== null) {
                const isPsapInLocalCache = this.serverPsaps.find((psap) => {
                    return psap.id === value
                })
                if (!isPsapInLocalCache) {
                    const { data } = await getPsap(value)
                    this.serverPsaps = [data]
                }
            }

            this.selected = value
        },
        search(input: string) {
            if (input === '') {
                this.selected = null
            }
            input && this.searchPsaps(input)
        },
        selected() {
            this.$emit('input', this.selected)
        }
    },
    methods: {
        async searchPsaps(input: string) {
            if (this.selected == null) {
                this.loading = true

                try {
                    const response = await searchPsaps({
                        searchTerm: input
                    })
                    this.serverPsaps = response.data
                } catch (error) {
                    this.error = error
                }
                this.loading = false
            }
        },
        itemName(item: Psap): string {
            if (item.id === false) {
                return `${item.name}`
            }

            return `${item.name}, ${item.state}`
        }
    },
    computed: {
        selectableItems(): any[] {
            // Add option for PSAP not being found.
            const notListedOption = {
                id: false,
                name: (this as any).$t('homepage.psapSelect.notListed')
            }

            // Don't show any options unless searching.
            // if (!this.search && this.selected === null) {
            //     return []
            // }

            // If the user has selected an option, v-autocomplete will
            // not show any of the normal items. This prevents it from
            // immediately showing the 'not listed' after a selection
            if (this.selected) {
                return [...this.serverPsaps]
            }

            // Return list of selectable items including the 'not found' option
            return [notListedOption, ...this.serverPsaps]
        }
    }
})
</script>

<style lang="scss" scoped>
.input-group {
    margin-bottom: 1.5rem;
}

.input-group label {
    display: block;
    margin-bottom: 0.75rem;
    font-weight: 500;
    color: #1f2937;
    font-size: 0.67375rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.v-autocomplete ::v-deep .v-input__control {
    border: 1px solid #d1d5db !important;
    border-radius: 8px !important;
    background: #f9fafb !important;
    box-shadow: none !important;
    height: auto !important;
    min-height: auto !important;
}

.v-autocomplete ::v-deep .v-input__slot {
    padding: 0.75rem !important;
    min-height: auto !important;
    background: #f9fafb !important;
    font-size: 0.85rem !important;
}

.v-autocomplete ::v-deep .v-input__slot::before,
.v-autocomplete ::v-deep .v-input__slot::after {
    display: none !important;
}

.v-autocomplete ::v-deep .v-text-field__details {
    display: none !important;
}

.v-autocomplete ::v-deep .v-input__underline {
    display: none !important;
}

.v-autocomplete ::v-deep .v-text-field__slot input {
    font-size: 0.7rem !important;
    color: #1f2937 !important;
}

.v-autocomplete ::v-deep .v-text-field__slot input::placeholder {
    font-size: 0.55rem !important;
    color: #9ca3af !important;
}

.v-autocomplete ::v-deep .v-input--is-focused .v-input__control {
    border-color: #1876d1 !important;
    background: #d1d5db !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
}

.v-autocomplete ::v-deep .v-label {
    display: none !important;
}
</style>